import React from "react";
import { partial } from "lodash";
import { css } from "@emotion/core";
import {
  ApiKeys,
  PaymentSourceInput,
  PayPalPaymentSource,
  SupportedPaymentMethod,
  PaymentSourceType,
} from "@zapier/payment/esm/types";
import {
  Button,
  ButtonGroup,
  Link,
  Text,
  useTheme,
} from "@zapier/design-system";
import PaymentMethodImage from "@zapier/payment/esm/components/PaymentMethodImage";
import PaypalButton from "@zapier/payment/esm/components/PaypalButton";
import PaypalInfo from "@zapier/payment/esm/components/PaypalInfo";
import { useSize } from "@zapier/payment/esm/hooks/useSize";
import PaymentMethod from "../PaymentMethod";
import PaymentMethodSelect from "../PaymentMethodSelect";

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Currently select method */
  currentMethod: PaymentSourceType | null;
  /** Hide switch component button */
  hideSwitch: boolean;
  /** Show editor */
  isEditing: boolean;
  /** Call to canel edit */
  onCancelEdit: () => void;
  /** Call to start edit */
  onEdit: () => void;
  /** Call when editor throws an error */
  onErrorEdit: (error: Error, method?: PaymentSourceType) => void;
  /** Call to save current edit */
  onSaveEdit: (paypalDetails: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType) => void;
  /** User's currently saved paypal details */
  savedSource: PayPalPaymentSource | null;
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>;
  /** The array of payment methods selectable from the "Switch Payment Method" dropdown */
  switchMethodOptions: SupportedPaymentMethod[];
};

const buttonGroupWrapper = css`
  margin-top: 15px;
  margin-bottom: 15px;
`;

const contentCss = css`
  text-align: center;
`;

const labelCss = css`
  margin-right: 20px;
`;

const renderLabel = (props: Props, isRebrand: boolean) =>
  props.savedSource && !props.isEditing ? (
    <PaypalInfo paypal={props?.savedSource?.details} />
  ) : (
    <React.Fragment>
      {!isRebrand && (
        <PaymentMethodImage
          imageType={PaymentSourceType.paypal}
          isSmall={true}
        />
      )}
      <span css={labelCss}>
        <Text tag="h3" type={isRebrand ? "paragraph1Bold" : "paragraph1Bold"}>
          Connect PayPal
        </Text>
      </span>
    </React.Fragment>
  );

const renderAside = (props: Props, isSmall: boolean) => {
  if (!props.isEditing) {
    return (
      <Button
        ariaLabel="Edit payment method"
        color="secondary"
        onClick={props.onEdit}
      >
        {props.savedSource ? `Edit${!isSmall ? " payment method" : ""}` : "Add"}
      </Button>
    );
  } else {
    return props.hideSwitch ? null : (
      <PaymentMethodSelect
        currentMethod={props.currentMethod}
        onChange={props.onSelectSource}
        supportedMethods={props.switchMethodOptions}
      />
    );
  }
};

const renderAsideRebrand = (props: Props) => {
  if (!props.isEditing) {
    return (
      <Button
        ariaLabel={
          props.savedSource
            ? "Edit payment information"
            : "Add payment information"
        }
        color="secondary"
        onClick={props.onEdit}
        size="small"
      >
        {props.savedSource ? "Edit payment information" : "Add"}
      </Button>
    );
  } else {
    return props.hideSwitch ? null : (
      <PaymentMethodSelect
        currentMethod={props.currentMethod}
        onChange={props.onSelectSource}
        supportedMethods={props.switchMethodOptions}
      />
    );
  }
};

/* Rebrand cleanup required */
const PaymentMethodPaypal: React.FC<Props> = props => {
  const { isRebrand } = useTheme();
  const size = useSize();
  const isSmall = size === "small";

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={
        isRebrand
          ? partial(renderAsideRebrand, props)
          : partial(renderAside, props, isSmall)
      }
      renderLabel={partial(renderLabel, props, isRebrand)}
    >
      <div css={contentCss}>
        {isRebrand ? (
          <Text fontWeight={600} type="paragraph1">
            {props.savedSource
              ? "You can reconnect to use a different PayPal account."
              : "Connect your PayPal account."}
          </Text>
        ) : (
          <Text type="paragraph3">
            {props.savedSource
              ? "You can reconnect to use a different PayPal account."
              : "Please connect your PayPal account."}
          </Text>
        )}
        <div css={buttonGroupWrapper}>
          <ButtonGroup
            columnSize={isSmall ? "100%" : "minmax(100px, 250px)"}
            justify="center"
          >
            <Button
              ariaLabel="Cancel"
              color="secondary"
              isFullWidth={true}
              onClick={props.onCancelEdit}
              {...(isRebrand ? { size: "small" } : {})}
            >
              Cancel
            </Button>
            <PaypalButton
              apiKey={props.apiKeys.braintree}
              onAuthorize={props.onSaveEdit}
              onError={props.onErrorEdit}
            />
          </ButtonGroup>
        </div>
        {!isRebrand && (
          <Link
            href="https://www.paypal.com/cgi-bin/webscr?cmd=xpt/Marketing/popup/OLCWhatIsPayPal-outside"
            target="_blank"
          >
            What is PayPal?
          </Link>
        )}
      </div>
    </PaymentMethod>
  );
};

export default PaymentMethodPaypal;
